import React from "react";
import type { Ref } from "react";
import type { StyleProp, TextProps, TextStyle } from "react-native";
// eslint-disable-next-line no-restricted-imports
import { Text as RNText } from "react-native";
import type { AnimatedStyle } from "react-native-reanimated";
import Animated from "react-native-reanimated";
import { type UnistylesVariants } from "react-native-unistyles";

import $ from "./Text.styles";

type Variants = UnistylesVariants<typeof $>;

type Props = Omit<TextProps, "style"> &
  Variants & {
    style?: StyleProp<TextStyle>;
    ref?: Ref<RNText>;
  };

type AnimatedProps = Omit<TextProps, "style"> &
  Variants & {
    style?: StyleProp<AnimatedStyle<TextStyle>>;
    ref?: Ref<Animated.Text>;
  };

/**
 * Static text
 *
 * Renders a plain React Native `Text`. Use this everywhere text does not
 * animate, it skips the Reanimated wrapper `Text.Animated` sets up.
 */
const TextBase = ({ children, style, preset, ref, ...restProps }: Props) => {
  $.useVariants({ preset });

  const isBalanced = preset === "body";

  const textBreakStrategy = isBalanced ? "balanced" : undefined;

  // iOS has no balanced strategy; "standard" is its best-quality set (push-out to avoid orphans + CJK/Hangul handling)
  const lineBreakStrategyIOS = isBalanced ? "standard" : undefined;

  return (
    <RNText
      ref={ref}
      textBreakStrategy={textBreakStrategy}
      lineBreakStrategyIOS={lineBreakStrategyIOS}
      style={[$.text, style]}
      {...restProps}
    >
      {children}
    </RNText>
  );
};

/**
 * Animated text
 *
 * Renders a Reanimated `Animated.Text`, so a `useAnimatedStyle` / shared-value
 * `style` animates on the UI thread with no React re-render.
 *
 * Use only when the text itself animates; prefer `Text`, which is cheaper.
 */
const AnimatedTextBase = ({
  children,
  style,
  preset,
  ref,
  ...restProps
}: AnimatedProps) => {
  $.useVariants({ preset });

  const isBalanced = preset === "body";

  const textBreakStrategy = isBalanced ? "balanced" : undefined;

  // iOS has no balanced strategy; "standard" is its best-quality set (push-out to avoid orphans + CJK/Hangul handling)
  const lineBreakStrategyIOS = isBalanced ? "standard" : undefined;

  return (
    <Animated.Text
      ref={ref}
      textBreakStrategy={textBreakStrategy}
      lineBreakStrategyIOS={lineBreakStrategyIOS}
      style={[$.text, style]}
      {...restProps}
    >
      {children}
    </Animated.Text>
  );
};

const Text = Object.assign(TextBase, {
  Animated: AnimatedTextBase,
});

export default Text;
